import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  name: "", code: "", category: "", description: "",
  unitPrice: "", currency: "USD", usageUnit: "", taxPct: "", commissionPct: "",
  manufacturer: "", vendorId: "",
  quantityInStock: "", quantityOrdered: "", quantityInDemand: "", reorderLevel: "",
  salesStartDate: "", salesEndDate: "",
  supportStartDate: "", supportEndDate: "",
  isActive: true,
  handlerId: "",
  tags: "",
};
type ProductForm = typeof EMPTY;

export default function ProductNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ProductForm>(EMPTY);

  useEffect(() => {
    api.get("/accounts", { params: { pageSize: 100 } }).then((r) => setVendors(r.data.data ?? []));
    api.get("/users").then((r) => setUsers(r.data.data ?? []));
  }, []);

  function set<K extends keyof ProductForm>(k: K, v: ProductForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save(mode: "save" | "saveAndNew") {
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "tags") payload.tags = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
        else if (["unitPrice", "taxPct", "commissionPct", "quantityInStock", "quantityOrdered", "quantityInDemand", "reorderLevel"].includes(k)) payload[k] = Number(v);
        else payload[k] = v;
      }
      await api.post("/products", payload);
      qc.invalidateQueries({ queryKey: ["/products"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/products");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create product.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Create IT Product" description="Add a new IT product to the catalog." />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Section title="IT Product information">
            <Field id="name" label="Product name" required className="md:col-span-2">
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field id="code" label="Product code (SKU)">
              <Input id="code" value={form.code} onChange={(e) => set("code", e.target.value)} />
            </Field>
            <Field id="category" label="Category">
              <Select id="category" value={form.category} onChange={(e) => set("category", e.target.value)}>
                <option value="">—</option>
                <option value="Hardware">Hardware</option>
                <option value="SaaS">SaaS</option>
                <option value="License">License</option>
                <option value="Service">Service</option>
                <option value="Subscription">Subscription</option>
                <option value="Support">Support</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
            <Field id="manufacturer" label="Manufacturer">
              <Input id="manufacturer" value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} />
            </Field>
            <Field id="vendorId" label="Vendor">
              <Select id="vendorId" value={form.vendorId} onChange={(e) => set("vendorId", e.target.value)}>
                <option value="">— None —</option>
                {vendors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
            <Field id="handlerId" label="Handler">
              <Select id="handlerId" value={form.handlerId} onChange={(e) => set("handlerId", e.target.value)}>
                <option value="">— None —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set("isActive", e.target.checked)}
              />
              <label htmlFor="isActive" className="text-sm font-medium">Product is active</label>
            </div>
          </Section>

          <Section title="Pricing">
            <Field id="unitPrice" label="Unit price">
              <Input id="unitPrice" type="number" min="0" step="0.01" value={form.unitPrice} onChange={(e) => set("unitPrice", e.target.value)} />
            </Field>
            <Field id="currency" label="Currency">
              <Select id="currency" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                <option value="USD">USD</option>
                <option value="INR">INR</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </Select>
            </Field>
            <Field id="usageUnit" label="Usage unit">
              <Select id="usageUnit" value={form.usageUnit} onChange={(e) => set("usageUnit", e.target.value)}>
                <option value="">—</option>
                <option value="each">Each</option>
                <option value="hour">Hour</option>
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
                <option value="seat">Seat</option>
                <option value="seat-month">Seat-month</option>
                <option value="seat-year">Seat-year</option>
                <option value="user-year">User-year</option>
                <option value="GB">GB</option>
              </Select>
            </Field>
            <Field id="taxPct" label="Tax (%)">
              <Input id="taxPct" type="number" min="0" max="100" step="0.1" value={form.taxPct} onChange={(e) => set("taxPct", e.target.value)} />
            </Field>
            <Field id="commissionPct" label="Commission rate (%)">
              <Input id="commissionPct" type="number" min="0" max="100" step="0.1" value={form.commissionPct} onChange={(e) => set("commissionPct", e.target.value)} />
            </Field>
          </Section>

          <Section title="Inventory">
            <Field id="quantityInStock" label="Qty in stock">
              <Input id="quantityInStock" type="number" min="0" value={form.quantityInStock} onChange={(e) => set("quantityInStock", e.target.value)} />
            </Field>
            <Field id="reorderLevel" label="Reorder level">
              <Input id="reorderLevel" type="number" min="0" value={form.reorderLevel} onChange={(e) => set("reorderLevel", e.target.value)} />
            </Field>
            <Field id="quantityOrdered" label="Qty ordered">
              <Input id="quantityOrdered" type="number" min="0" value={form.quantityOrdered} onChange={(e) => set("quantityOrdered", e.target.value)} />
            </Field>
            <Field id="quantityInDemand" label="Qty in demand">
              <Input id="quantityInDemand" type="number" min="0" value={form.quantityInDemand} onChange={(e) => set("quantityInDemand", e.target.value)} />
            </Field>
          </Section>

          <Section title="Lifecycle dates">
            <Field id="salesStartDate" label="Sales start">
              <Input id="salesStartDate" type="date" value={form.salesStartDate} onChange={(e) => set("salesStartDate", e.target.value)} />
            </Field>
            <Field id="salesEndDate" label="Sales end">
              <Input id="salesEndDate" type="date" value={form.salesEndDate} onChange={(e) => set("salesEndDate", e.target.value)} />
            </Field>
            <Field id="supportStartDate" label="Support start">
              <Input id="supportStartDate" type="date" value={form.supportStartDate} onChange={(e) => set("supportStartDate", e.target.value)} />
            </Field>
            <Field id="supportEndDate" label="Support end">
              <Input id="supportEndDate" type="date" value={form.supportEndDate} onChange={(e) => set("supportEndDate", e.target.value)} />
            </Field>
          </Section>

          <Section title="Categorization & description">
            <div className="md:col-span-2">
              <label htmlFor="tags" className="text-sm font-medium">Tags (comma-separated)</label>
              <Input id="tags" placeholder="enterprise, strategic, renewable" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="text-sm font-medium">Description</label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.name}
            onCancel={() => navigate("/products")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
